import { computed, inject, Injectable, signal } from '@angular/core';
import { ErrorService } from '@interacta-shared/data-access-error';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
    IExternalLinksCategory,
    IExternalLinksCategoryType,
} from '../models/external-link.model';
import { ExternalLinksService } from './external-links.service';

@Injectable({ providedIn: 'root' })
export class ExternalLinksStateService {
    readonly state = undefined;
    private readonly externalLinksService = inject(ExternalLinksService);
    private readonly errorService = inject(ErrorService);
    private initialized = false;

    readonly externalLinksCategories = signal<IExternalLinksCategory[] | null>(
        null,
    );

    readonly adminAppUrl = computed(() => {
        const categories = this.externalLinksCategories();
        const adminCategory = categories?.find(
            (c) => c.type === IExternalLinksCategoryType.ADMINISTRATION,
        );
        return adminCategory?.links?.length ? adminCategory.links[0].url : '';
    });

    initialize(): void {
        if (this.initialized) return;
        this.initialized = true;
        this.externalLinksService
            .getCategories()
            .pipe(
                catchError((err) => {
                    this.initialized = false;
                    this.errorService.handle(err);
                    return EMPTY;
                }),
            )
            .subscribe((val) => this.externalLinksCategories.set(val));
    }

    flush(): void {
        this.initialized = false;
        this.externalLinksCategories.set(null);
    }

    togglePinLink(linkId: number, pinned: boolean): void {
        this.externalLinksCategories.update((categories) => {
            if (!categories) return categories;
            return categories.map((category) => ({
                ...category,
                links: category.links.map((link) =>
                    link.id === linkId ? { ...link, pinned } : link,
                ),
            }));
        });
        this.externalLinksService.pinLink(linkId, pinned).subscribe({
            error: (err) => {
                this.errorService.handle(err);
                this.externalLinksCategories.update((categories) => {
                    if (!categories) return categories;
                    return categories.map((category) => ({
                        ...category,
                        links: category.links.map((link) =>
                            link.id === linkId
                                ? { ...link, pinned: !pinned }
                                : link,
                        ),
                    }));
                });
            },
        });
    }
}
